import CustomCheckbox from '../../CustomCheckbox'
import { TableColumn } from '../types'

import { ColumnMenu } from './ColumnMenu'

/**
 * Raised (convex) header fill — light top, slightly deeper bottom; opaque for sticky cells.
 * Active sort column overrides via HEADER_SORTED_CLASS (not row-wide).
 */
export const HEADER_CELL_BACKGROUND_CONVEX =
  'linear-gradient(to bottom, #ffffff 0%, #f3f5f8 100%)'

export const HEADER_CELL_BASE_CLASS =
  'bg-white bg-gradient-to-b from-white to-[#f3f5f8]'

export const HEADER_SORTED_CLASS =
  'bg-brand-primary-subtle from-brand-primary-subtle to-brand-primary-subtle/90 text-brand-primary-strong'

export const HEADER_SORTABLE_HOVER_CLASS =
  'cursor-pointer hover:from-brand-primary-subtle/70 hover:to-brand-primary-subtle/40 hover:text-brand-primary'

interface TableHeaderProps<T> {
  headerClassName: string
  selectable: boolean
  selectAll: boolean
  dataLength: number
  handleSelectAllWrapper: (checked: boolean) => void
  visibleColumnsList: TableColumn<T>[]
  columnWidths: Record<string, number>
  resizingColumn: string | null
  headerRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>
  handleResizeStart: (columnKey: string, e: React.MouseEvent) => void
  sortable: boolean
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  onSort?: (column: string) => void
  showColumnManagement: boolean
  setShowColumnMenu: React.Dispatch<React.SetStateAction<boolean>>
  columnMenuRef: React.RefObject<HTMLDivElement>
  showColumnMenu: boolean
  columnMenuPosition: { top: number; right: number } | null
  columnsWithRequired: TableColumn<T>[]
  visibleColumns: Set<string>
  columnDropdownRef: React.RefObject<HTMLDivElement>
  toggleColumnVisibilityWrapper: (columnKey: string) => void
}

// Helper function to calculate minimum width based on column label
function calculateMinColumnWidth<T>(column: TableColumn<T>): string {
  // Base padding: px-4 = 16px on each side = 32px total
  // For actions column: px-0 = 0px
  const horizontalPadding = column.key === 'actions' ? 0 : 32
  
  // Space for sort icon and gap: ~24px (icon ~16px + gap ~8px)
  const sortIconSpace = column.sortable ? 24 : 0
  
  // Calculate text width: approximate 8px per character for uppercase text-xs with tracking-wider
  const textWidth = column.label.length * 8
  
  // Minimum width = text + padding + sort icon + buffer
  const minWidth = textWidth + horizontalPadding + sortIconSpace + 16 // 16px buffer
  
  return `${Math.max(minWidth, 80)}px` // Absolute minimum of 80px
}

export function TableHeader<T>({
  headerClassName,
  selectable,
  selectAll,
  dataLength,
  handleSelectAllWrapper,
  visibleColumnsList,
  columnWidths,
  resizingColumn,
  headerRefs,
  handleResizeStart,
  sortable,
  sortColumn,
  sortDirection,
  onSort,
  showColumnManagement,
  setShowColumnMenu,
  columnMenuRef,
  showColumnMenu,
  columnMenuPosition,
  columnsWithRequired,
  visibleColumns,
  columnDropdownRef,
  toggleColumnVisibilityWrapper,
}: TableHeaderProps<T>) {
  const HEADER_STICKY_Z = 31

  return (
    <>
    <thead className={`sticky top-0 z-30 ${headerClassName}`}>
      <tr className="border-b-2 border-[#5B6B8C]/20 relative">
        {/* Select All Checkbox */}
        {selectable && (
          <th
            className={`py-4 px-4 w-12 ${HEADER_CELL_BASE_CLASS}`}
            style={{ background: HEADER_CELL_BACKGROUND_CONVEX }}
          >
            <CustomCheckbox
              checked={selectAll && dataLength > 0}
              onChange={(checked) => handleSelectAllWrapper(checked)}
              size="md"
            />
          </th>
        )}

        {/* Column Headers */}
        {visibleColumnsList.map((column) => {
          const isSticky = column.sticky === 'left' || column.sticky === 'right'
          const stickyClass = isSticky ? `sticky ${column.sticky}-0` : ''
          const stickyStyle = isSticky ? { zIndex: HEADER_STICKY_Z } : {}
          const alignClass = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
          
          // Check if this is the Actions column (where we'll add column management button)
          const isActionsColumn = column.key === 'actions'
          const isSorted = sortColumn === column.key
          const canSort =
            !resizingColumn && column.sortable && sortable && !isActionsColumn
          const headerCellBgClass = [
            HEADER_CELL_BASE_CLASS,
            stickyClass,
            isSorted ? HEADER_SORTED_CLASS : '',
            canSort ? HEADER_SORTABLE_HOVER_CLASS : '',
          ]
            .filter(Boolean)
            .join(' ')
          const headerCellStyle = isSorted
            ? { ...stickyStyle, background: undefined }
            : { ...stickyStyle, background: HEADER_CELL_BACKGROUND_CONVEX }
          
          // Calculate header width styles - auto width with min width based on label, or resized width
          const minWidth = calculateMinColumnWidth(column)
          const headerWidth = columnWidths[column.key]
            ? { width: `${columnWidths[column.key]}px`, minWidth: `${columnWidths[column.key]}px` }
            : column.width 
              ? (() => {
                  const widthStr = typeof column.width === 'number' ? `${column.width}px` : column.width
                  const minWidthStr = column.minWidth ? (typeof column.minWidth === 'number' ? `${column.minWidth}px` : column.minWidth) : minWidth
                  return { width: widthStr, minWidth: minWidthStr }
                })()
              : { width: 'auto', minWidth }

          return (
            <th
              key={column.key}
              ref={(el) => {
                headerRefs.current[column.key] = el
              }}
              className={`py-4 ${column.key === 'actions' ? 'px-0' : 'px-4'} font-semibold text-[#5B6B8C] text-xs uppercase tracking-wider whitespace-nowrap ${headerCellBgClass} ${column.key === 'actions' ? 'text-center' : alignClass} relative`}
              aria-label={column.key === 'actions' && !column.label ? 'Actions' : undefined}
              style={{
                ...headerWidth,
                ...headerCellStyle,
              }}
              onClick={(e) => {
                // Don't sort if resizing
                if (resizingColumn) {
                  e.preventDefault()
                  e.stopPropagation()
                  return
                }
                if (column.sortable && sortable && onSort) {
                  onSort(column.key)
                }
              }}
            >
              <div className="flex items-center gap-2 justify-between w-full">
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap">{column.label}</span>
                  {column.sortable && sortable && (
                    <span className="ml-1.5 text-xs leading-none inline-block" style={{ width: '12px', textAlign: 'left' }}>
                      {sortColumn === column.key ? (
                        <span className="text-green-600">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      ) : (
                        <span className="text-gray-400 inline-flex items-center" style={{ lineHeight: '1', fontSize: '0.75rem' }}>
                          <span>↑</span>
                          <span style={{ marginLeft: '-2px' }}>↓</span>
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {/* Column Management Button - only in Actions column */}
                {isActionsColumn && showColumnManagement && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowColumnMenu(!showColumnMenu)
                    }}
                    className="ml-auto px-2 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0"
                    style={{ outline: 'none' }}
                    title="Manage columns"
                    onFocus={(e) => {
                      e.target.blur()
                    }}
                    ref={columnMenuRef}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Column resize handle */}
              {!isActionsColumn && (
                <div
                  className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 transition-colors ${
                    resizingColumn === column.key ? 'bg-blue-400' : ''
                  }`}
                  style={{
                    zIndex: 10,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleResizeStart(column.key, e)
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  title="Drag to resize column"
                />
              )}
              {/* For Actions column, add resize handle but offset it to avoid column management button */}
              {isActionsColumn && showColumnManagement && (
                <div
                  className={`absolute top-0 right-[35px] w-1 h-full cursor-col-resize hover:bg-blue-400 transition-colors ${
                    resizingColumn === column.key ? 'bg-blue-400' : ''
                  }`}
                  style={{
                    zIndex: 10,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleResizeStart(column.key, e)
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  title="Drag to resize column"
                />
              )}
            </th>
          )
        })}

      </tr>
    </thead>
    <ColumnMenu
      showColumnManagement={showColumnManagement}
      showColumnMenu={showColumnMenu}
      columnMenuPosition={columnMenuPosition}
      columnsWithRequired={columnsWithRequired}
      visibleColumns={visibleColumns}
      columnDropdownRef={columnDropdownRef}
      toggleColumnVisibilityWrapper={toggleColumnVisibilityWrapper}
    />
    </>
  )
}
